import { AlertTriangle, Save, Search, TrashIcon, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'

import { AllFiltersArea } from './filter.types'
import type { UrlLengthCheckResult } from './hooks/useUrlLengthCheck'
import { BulkAction, SearchFilter } from '@api/orval/apiHooks'
import { FormControl, FormField, FormItem } from '@components/ui/Form'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  SelectTrigger,
} from '@fp/ui-kit'
import { iconStroke } from '@lib/iconProps'
import type { UrlLengthStatus } from '@lib/urlLength'

const iconsCSS = '!size-4 !-mr-1'

export interface FilterActionsProps<T extends FieldValues> {
  isLoading: boolean
  filterCount?: number
  bulkActions?: BulkAction[]
  onReset: () => void
  form: UseFormReturn<T>
  filtersArea: AllFiltersArea
  savedFilters: SearchFilter[]
  onOpenSaveModal: () => void
  onDeleteFilter: (filterId: number) => void
  onSavedFilterChange: (value: string | null) => void
  urlLengthCheck: UrlLengthCheckResult
}

const statusConfig: Record<
  Exclude<UrlLengthStatus, 'ok'>,
  {
    iconColor: string
    borderColor: string
    barColor: string
    title: string
    description: string
  }
> = {
  warning: {
    iconColor: 'text-warning',
    borderColor: 'border-warning',
    barColor: 'bg-warning',
    title: 'URL approaching limit',
    description: 'You have many filters selected. Consider reducing them to avoid request failures.',
  },
  error: {
    iconColor: 'text-destructive-foreground',
    borderColor: 'border-destructive-foreground',
    barColor: 'bg-destructive-foreground',
    title: 'URL limit exceeded',
    description:
      'Too many filters selected. The request will be rejected by the server. Remove some filters to continue.',
  },
}

function useUrlLengthPopover(status: UrlLengthStatus) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (status === 'ok') {
      setIsOpen(false)
    } else {
      setIsOpen(true)
    }
  }, [status])

  return { isOpen, setIsOpen }
}

export const FilterActions = <T extends FieldValues>({
  isLoading,
  filterCount,
  // bulkActions,
  onReset,
  form,
  // filtersArea,
  savedFilters,
  onOpenSaveModal,
  onDeleteFilter,
  onSavedFilterChange,
  urlLengthCheck,
}: FilterActionsProps<T>) => {
  const hasIssue = urlLengthCheck.status !== 'ok'
  const isBlocked = urlLengthCheck.status === 'error'
  const { isOpen, setIsOpen } = useUrlLengthPopover(urlLengthCheck.status)

  const config = hasIssue ? statusConfig[urlLengthCheck.status as Exclude<UrlLengthStatus, 'ok'>] : null
  const filterLabel = `Filter results${filterCount !== undefined ? ` (${filterCount})` : ''}`

  const buttonContent = isLoading ? (
    <>Filtering...</>
  ) : (
    <>
      <Search className={iconsCSS} />
      {filterLabel}
      {config && <AlertTriangle className={`size-4 shrink-0 absolute right-3 ${config.iconColor}`} />}
    </>
  )

  const renderSubmitButton = () => {
    if (!hasIssue) {
      return (
        <Button type='submit' variant='primary' size='block' disabled={isLoading} isLoading={isLoading}>
          {buttonContent}
        </Button>
      )
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Wrapper div captures hover even when button is disabled (error state) */}
          <div
            className={`w-full ${isBlocked ? 'cursor-not-allowed' : ''}`}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <Button
              type={isBlocked ? 'button' : 'submit'}
              variant={isBlocked ? 'destructive' : 'primary'}
              size='block'
              disabled={isLoading || isBlocked}
              isLoading={isLoading}
              className={`relative ${isBlocked ? 'pointer-events-none' : ''}`}
            >
              {buttonContent}
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent side='right' align='center' className={`w-64 text-sm space-y-2 ${config!.borderColor}`}>
          <p className='font-medium'>{config!.title}</p>
          <div className='rounded bg-muted p-2 space-y-1.5'>
            <div className='flex justify-between items-center text-xs'>
              <span className='text-muted-foreground'>URL size</span>
              <span className='font-medium tabular-nums'>
                {urlLengthCheck.formattedCurrent} / {urlLengthCheck.formattedLimit}
              </span>
            </div>
            <div className='w-full bg-background rounded-full h-1.5'>
              <div
                className={`h-1.5 rounded-full transition-all ${config!.barColor}`}
                style={{ width: `${urlLengthCheck.percentageUsed}%` }}
              />
            </div>
          </div>
          <p className='text-xs text-muted-foreground'>{config!.description}</p>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className='sticky bottom-0 pb-4 flex flex-col mt-4 gap-2 bg-background'>
      {renderSubmitButton()}

      {/* {bulkActions && <BulkActionsMenu bulkActions={bulkActions} filterArea={filtersArea} />} */}

      <div className='flex flex-row gap-2'>
        <Button variant='default' type='button' size='block' onClick={onReset}>
          <X className={iconsCSS} />
          Clear Filters
        </Button>

        <Button variant='default' type='button' size='block' onClick={onOpenSaveModal}>
          <Save className={iconsCSS} strokeWidth={iconStroke.regular} />
          Save filters
        </Button>
      </div>

      {savedFilters && savedFilters.length > 0 && (
        <FormField
          control={form.control}
          name={'saved_filters' as Path<T>}
          render={({ field }) => (
            <FormItem className='flex flex-col gap-2'>
              <FormControl>
                <Select {...field} onValueChange={onSavedFilterChange}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select filter' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {savedFilters.map((filter) => (
                        <span key={filter.id} className='flex flex-row justify-between items-center w-full'>
                          <SelectItem value={String(filter.id)}>{filter.name}</SelectItem>
                          <TrashIcon
                            className='size-5 min-w-5 min-h-5 ml-2 text-destructive-foreground hover:text-destructive hover:bg-destructive-foreground rounded-sm p-0.5'
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              onDeleteFilter(filter.id)
                            }}
                          />
                        </span>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  )
}
